/**
 * Form Validation Utilities
 * Client-side validation for demo request form
 */

/**
 * Validate email format with stricter rules
 * Blocks common test/fake emails
 */
export function validateEmail(email: string): boolean {
  if (!email || email.trim() === '') return false;
  
  const trimmed = email.trim().toLowerCase();
  
  // Block common test/fake email patterns
  const blockedPatterns = [
    /^test@/,
    /^fake@/,
    /^asdf@/,
    /^qwerty@/,
    /test\.com$/,
    /fake\.com$/,
    /example\.com$/,
    /temp\.com$/,
    /^.*@test$/,
  ];
  
  for (const pattern of blockedPatterns) {
    if (pattern.test(trimmed)) {
      return false;
    }
  }
  
  // RFC 5322 compliant email regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  // Must have valid format and proper TLD
  if (!emailRegex.test(trimmed)) return false;
  
  // Must have a TLD with at least 2 characters
  const parts = trimmed.split('@');
  if (parts.length !== 2) return false;
  
  const domain = parts[1];
  const domainParts = domain.split('.');
  if (domainParts.length < 2) return false;
  
  const tld = domainParts[domainParts.length - 1];
  if (tld.length < 2) return false;
  
  return true;
}

/**
 * Validate phone number (international format) - OPTIONAL
 * Accepts: +31612345678, 0612345678, +31 6 12345678, etc.
 */
export function validatePhone(phone: string): boolean {
  // Phone is now optional
  if (!phone || phone.trim() === '') return true;
  
  // Remove spaces, dashes, parentheses
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  
  // Must have 8-15 digits, optionally starting with +
  const phoneRegex = /^\+?[0-9]{8,15}$/;
  return phoneRegex.test(cleaned);
}

/**
 * Validate required field (non-empty)
 */
export function validateRequired(value: string): boolean {
  return value !== null && value !== undefined && value.trim() !== '';
}

/**
 * Validate company name (2-100 characters)
 */
export function validateCompany(company: string): boolean {
  if (!validateRequired(company)) return false;
  const trimmed = company.trim();
  return trimmed.length >= 2 && trimmed.length <= 100;
}

/**
 * Validate name (2-50 characters, letters and common characters only)
 */
export function validateName(name: string): boolean {
  if (!validateRequired(name)) return false;
  const trimmed = name.trim();
  
  // Allow letters, spaces, hyphens, apostrophes, dots
  const nameRegex = /^[a-zA-ZÀ-ÿ\s\-'.]{2,50}$/;
  return nameRegex.test(trimmed);
}

/**
 * Validate optional message field (max 1000 characters)
 */
export function validateMessage(message: string): boolean {
  // Message is optional, so empty is valid
  if (!message || message.trim() === '') return true;
  return message.trim().length <= 1000;
}

/**
 * Validate business type selection
 */
export function validateBusinessType(businessType: string): boolean {
  const validTypes = [
    'retail',
    'hospitality',
    'healthcare',
    'education',
    'transport',
    'government',
    'events',
    'corporate',
    'other'
  ];
  
  return validTypes.includes(businessType);
}

/**
 * Validate revenue selection
 */
export function validateRevenue(revenue: string): boolean {
  const validRanges = [
    'unknown',
    '1-300k',
    '301k-500k',
    '501k-1M',
    '1M-2M',
    '2M-5M',
    '5M+'
  ];
  
  return validRanges.includes(revenue);
}

/**
 * Comprehensive form validation
 * Returns object with field-specific errors
 */
export interface FormErrors {
  [key: string]: string | undefined;
  firstname?: string;
  lastname?: string;
  email?: string;
  phone?: string;
  company?: string;
  business_type?: string;
  annual_revenue?: string;
  message?: string;
}

export function validateDemoForm(data: {
  firstname: string;
  lastname?: string;
  email: string;
  phone: string;
  company: string;
  business_type: string;
  annual_revenue: string;
  message?: string;
}): FormErrors {
  const errors: FormErrors = {};
  
  if (!validateName(data.firstname)) {
    errors.firstname = 'Voer een geldige voornaam in (2-50 karakters)';
  }
  
  if (data.lastname && !validateName(data.lastname)) {
    errors.lastname = 'Voer een geldige achternaam in (2-50 karakters)';
  }
  
  if (!validateEmail(data.email)) {
    errors.email = 'Voer een geldig zakelijk e-mailadres in (geen test-emails)';
  }
  
  // Phone is now optional, but if provided must be valid
  if (data.phone && !validatePhone(data.phone)) {
    errors.phone = 'Voer een geldig telefoonnummer in';
  }
  
  if (!validateCompany(data.company)) {
    errors.company = 'Voer een geldige bedrijfsnaam in (2-100 karakters)';
  }
  
  if (!data.business_type || data.business_type.trim() === '') {
    errors.business_type = 'Selecteer een branche';
  }
  
  if (!data.annual_revenue || data.annual_revenue.trim() === '') {
    errors.annual_revenue = 'Selecteer een omzetbereik';
  }
  
  if (data.message && !validateMessage(data.message)) {
    errors.message = 'Bericht mag maximaal 1000 karakters bevatten';
  }
  
  return errors;
}

/**
 * Validate contact form (simplified version without business_type and annual_revenue)
 */
export function validateContactForm(data: {
  firstname: string;
  lastname?: string;
  email: string;
  phone?: string;
  company: string;
  message?: string;
}): FormErrors {
  const errors: FormErrors = {};
  
  if (!validateName(data.firstname)) {
    errors.firstname = 'Voer een geldige voornaam in (2-50 karakters)';
  }
  
  if (data.lastname && !validateName(data.lastname)) {
    errors.lastname = 'Voer een geldige achternaam in (2-50 karakters)';
  }
  
  if (!validateEmail(data.email)) {
    errors.email = 'Voer een geldig zakelijk e-mailadres in (geen test-emails)';
  }
  
  // Phone is optional, but if provided must be valid
  if (data.phone && !validatePhone(data.phone)) {
    errors.phone = 'Voer een geldig telefoonnummer in';
  }
  
  if (!validateCompany(data.company)) {
    errors.company = 'Voer een geldige bedrijfsnaam in (2-100 karakters)';
  }
  
  if (data.message && !validateMessage(data.message)) {
    errors.message = 'Bericht mag maximaal 1000 karakters bevatten';
  }
  
  return errors;
}

/**
 * Check if form has any errors
 */
export function hasErrors(errors: FormErrors): boolean {
  return Object.keys(errors).length > 0;
}

